/*
 * Page frame of the Task generation tab: header with Save / Run, and the rules list.
 * index.jsx owns the state and handlers; this file is layout only.
 */
import React from 'react';
import type { ReactNode } from 'react';
import type { GenRule } from './gen_config';
import { RulesHeader } from './RuleRow';
import { rules_count_label } from './rule_card_labels';
import { Btn, LABEL_CLASS, TOUCH_CLASS } from './ui';

export function GenPageHeader({
  can_edit,
  dirty,
  saving,
  generating,
  save_disabled,
  save_title,
  run_disabled,
  run_title,
  on_save,
  on_run
}: {
  can_edit: boolean;
  dirty: boolean;
  saving: boolean;
  generating: boolean;
  save_disabled: boolean;
  save_title?: string;
  run_disabled: boolean;
  run_title?: string;
  on_save: () => void;
  on_run: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-line-subtle bg-surface-card px-[18px] py-3.5">
      <div className="min-w-0 flex-1 basis-[320px]">
        <h2 className="m-0 text-[18px] font-semibold text-content-primary">Task generation</h2>
        <div className="text-xs text-content-muted">
          Which sales tasks the generator creates, on which day, how often each rule re-scans and
          when its tasks fall due. Members, family-plan students and do-not-contact students are
          always skipped.
        </div>
        {can_edit ? null : (
          <div className="text-xs font-medium text-content-muted">
            View only — super users can edit
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 max-sm:w-full">
        {dirty && !saving ? (
          <span className="text-xs font-semibold text-state-warning-text max-sm:w-full">
            Unsaved changes
          </span>
        ) : null}
        {/* The spans carry the why-disabled tooltip; a disabled button fires no hover. */}
        <span title={run_title} className="max-sm:flex-1">
          <Btn onClick={on_run} disabled={run_disabled} className={`max-sm:!w-full ${TOUCH_CLASS}`}>
            {generating ? 'Starting…' : 'Run generation now'}
          </Btn>
        </span>
        <span title={save_title} className="max-sm:flex-1">
          <Btn
            variant="primary"
            onClick={on_save}
            disabled={save_disabled}
            className={`max-sm:!w-full ${TOUCH_CLASS}`}
          >
            {saving ? 'Saving…' : 'Save'}
          </Btn>
        </span>
      </div>
    </div>
  );
}

export function RulesSection({
  rules,
  notes,
  children
}: {
  rules: ReadonlyArray<Pick<GenRule, 'enabled'>>;
  notes: string[];
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-[10px] border border-line-subtle bg-surface-card">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-line-subtle bg-surface-sunken px-3.5 py-2">
        <h2 className={`m-0 ${LABEL_CLASS}`}>Rules</h2>
        <span className="text-xs text-content-muted">{rules_count_label(rules)}</span>
      </div>
      <RulesHeader />
      <div>{children}</div>
      {/* PRD triggers with no row of their own; the server sends the wording. */}
      {notes.length ? (
        <div className="border-t border-line-subtle px-3.5 py-2.5 text-[11.5px] text-content-muted">
          <div className="font-semibold">PRD triggers covered by an existing rule</div>
          {notes.map((note) => (
            <p key={note} className="m-0 mt-0.5">
              {note}
            </p>
          ))}
        </div>
      ) : null}
    </section>
  );
}
